import React from 'react'
import { useNavigate } from '@tanstack/react-router'
import { format, parseISO } from 'date-fns'
import { AlertTriangle, Inbox, PlusCircle, Edit, Trash2 } from 'lucide-react'
import { RevFinOpsClient } from '@/lib/revfinops-api'
import { NoResultsState } from './NoResultsState'

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface ClientsTableProps {
  clients: RevFinOpsClient[]
  loading: boolean
  error: string | null
  onRetry: () => void
  searchValue: string
  statusValue: string
  onClearFilters: () => void
  hasActiveFilters: boolean
  onEdit?: (client: RevFinOpsClient) => void
  onDelete?: (client: RevFinOpsClient) => void
}

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

/**
 * StatusBadge - Displays client status with appropriate styling
 */
function StatusBadge({ status }: { status: 'active' | 'inactive' | 'pending' }) {
  return (
    <span className={`status-${status}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  )
}

/**
 * LoadingSkeleton - Animated skeleton for loading state
 */
function LoadingSkeleton() {
  return (
    <div className="table-container">
      <table className="table">
        <thead className="table-header">
          <tr>
            <th>Client Name</th>
            <th>Status</th>
            <th>Created Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody className="table-body">
          {[...Array(8)].map((_, i) => (
            <tr key={i}>
              <td>
                <div className="loading-pulse h-4 w-32 rounded bg-gray-700" />
              </td>
              <td>
                <div className="loading-pulse h-6 w-20 rounded-full bg-gray-700" />
              </td>
              <td>
                <div className="loading-pulse h-4 w-24 rounded bg-gray-700" />
              </td>
              <td>
                <div className="loading-pulse h-4 w-16 rounded bg-gray-700" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * EmptyState - Shown when no clients exist
 */
function EmptyState() {
  const navigate = useNavigate()

  return (
    <div className="card flex flex-col items-center justify-center min-h-[400px] space-y-4">
      <Inbox className="h-16 w-16 text-brand-gray opacity-50" />
      <h3 className="text-xl font-display font-semibold text-white">
        No Clients Found
      </h3>
      <p className="text-brand-gray-light text-center max-w-md font-body">
        You haven't created any clients yet. Get started by creating your first client.
      </p>
      <button
        onClick={() => navigate({ to: '/revfinops/clients/new' })}
        className="btn-primary"
      >
        <PlusCircle size={20} />
        Create Client
      </button>
    </div>
  )
}

/**
 * ErrorState - Shown when API call fails
 */
function ErrorState({ error, onRetry }: { error: string; onRetry: () => void }) {
  return (
    <div className="card flex flex-col items-center justify-center min-h-[400px] space-y-4">
      <AlertTriangle className="h-16 w-16 text-error" />
      <h3 className="text-xl font-display font-semibold text-white">
        Error Loading Clients
      </h3>
      <p className="text-brand-gray-light text-center max-w-md font-body">
        {error || 'An unexpected error occurred while loading clients.'}
      </p>
      <button onClick={onRetry} className="btn-primary">
        Retry
      </button>
    </div>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * ClientsTable - Displays RevFinOps clients in a table format
 *
 * Features:
 * - Loading skeleton state
 * - Empty state with create button
 * - No results state when filters match nothing
 * - Error state with retry
 * - Clickable rows for navigation
 * - Status badges
 * - Date formatting
 * - Keyboard navigation support
 *
 * @param clients - Array of client objects (filtered)
 * @param loading - Loading state flag
 * @param error - Error message if any
 * @param onRetry - Callback to retry loading
 * @param searchValue - Current search filter value
 * @param statusValue - Current status filter value
 * @param onClearFilters - Callback to clear all filters
 * @param hasActiveFilters - Whether any filters are currently active
 */
export function ClientsTable({
  clients,
  loading,
  error,
  onRetry,
  searchValue,
  statusValue,
  onClearFilters,
  hasActiveFilters,
  onEdit,
  onDelete,
}: ClientsTableProps) {
  const navigate = useNavigate()

  // Helper function to format dates
  const formatDate = (dateString: string): string => {
    try {
      return format(parseISO(dateString), 'dd/MM/yyyy')
    } catch {
      return dateString
    }
  }

  // Handle row click navigation
  const handleRowClick = (clientId: string) => {
    navigate({ to: `/revfinops/clients/${clientId}` })
  }

  // Handle keyboard navigation (Enter/Space)
  const handleKeyDown = (e: React.KeyboardEvent, clientId: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleRowClick(clientId)
    }
  }

  // Loading state
  if (loading) {
    return <LoadingSkeleton />
  }

  // Error state
  if (error) {
    return <ErrorState error={error} onRetry={onRetry} />
  }

  // No results state (when filters are active but no matches)
  if (hasActiveFilters && clients.length === 0) {
    return (
      <NoResultsState
        search={searchValue}
        status={statusValue}
        onClearFilters={onClearFilters}
      />
    )
  }

  // Empty state (no clients at all)
  if (!clients || clients.length === 0) {
    return <EmptyState />
  }

  // Handle edit button click
  const handleEditClick = (e: React.MouseEvent, client: RevFinOpsClient) => {
    e.stopPropagation() // Prevent row click
    if (onEdit) {
      onEdit(client)
    }
  }

  // Handle delete button click
  const handleDeleteClick = (e: React.MouseEvent, client: RevFinOpsClient) => {
    e.stopPropagation() // Prevent row click
    if (onDelete) {
      onDelete(client)
    }
  }

  // Render table with data
  return (
    <div className="table-container">
      <table className="table">
        <thead className="table-header">
          <tr>
            <th>Client Name</th>
            <th>Status</th>
            <th>Created Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody className="table-body">
          {clients.map((client) => (
            <tr
              key={client.id}
              onClick={() => handleRowClick(client.id)}
              onKeyDown={(e) => handleKeyDown(e, client.id)}
              tabIndex={0}
              className="table-row-clickable"
              role="button"
              aria-label={`View details for ${client.name}`}
            >
              <td className="font-medium text-white">{client.name}</td>
              <td>
                <StatusBadge status={client.status} />
              </td>
              <td>{formatDate(client.createdAt)}</td>
              <td>
                <div className="flex items-center gap-2">
                  {onEdit && (
                    <button
                      onClick={(e) => handleEditClick(e, client)}
                      className="btn-ghost p-2"
                      aria-label={`Edit ${client.name}`}
                      title="Edit client"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                  )}
                  {onDelete && (
                    <button
                      onClick={(e) => handleDeleteClick(e, client)}
                      className="btn-ghost p-2 text-error hover:bg-error/20"
                      aria-label={`Delete ${client.name}`}
                      title="Delete client"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                  <span className="table-cell-action">
                    View Details
                  </span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default ClientsTable
